import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { MapPin, Plus, Pencil, Trash2, Ruler, Home } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const MAX_PROPERTIES = 20;

/**
 * CustomerPropertiesSection — Customer-360 panel that lists every property
 * for the customer (up to 20) and lets the owner:
 *   1. Add a new property (label + address + notes)
 *   2. Edit an existing property (label, address, notes)
 *   3. Delete a property (backend returns 409 if measurements are linked)
 *   4. "Measure on estimate" — opens the Smart Estimator scoped to that
 *       property via `?property_id=<id>`. Every measurement saved there
 *       is written under that property id, so the estimate carries the
 *       exact property tag through Estimate → Job → Invoice.
 *
 * Reuses the existing `/api/estimator/properties` CRUD (no new endpoints).
 * Server enforces the 20-property cap; UI mirrors it so the Add button
 * disables past the limit.
 */
export default function CustomerPropertiesSection({ customer }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);      // null = new, else the property being edited
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ label: "", address: "", latitude: null, longitude: null, notes: "" });

  const load = async () => {
    if (!customer?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/estimator/properties?customer_id=${customer.id}`);
      setItems(Array.isArray(data) ? data : (data?.properties || []));
    } catch { toast.error("Failed to load properties"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customer?.id]);

  const atLimit = items.length >= MAX_PROPERTIES;

  const openNew = () => {
    setEditing(null);
    setForm({
      // Sensible defaults: first property inherits the customer's address so
      // Josh doesn't retype it. Subsequent ones start blank.
      label: items.length === 0 ? "Main property" : "",
      address: items.length === 0 ? (customer?.address || "") : "",
      latitude: items.length === 0 ? (customer?.latitude ?? null) : null,
      longitude: items.length === 0 ? (customer?.longitude ?? null) : null,
      notes: "",
    });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      label: p.label || "",
      address: p.address || "",
      latitude: p.latitude ?? null,
      longitude: p.longitude ?? null,
      notes: p.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim() && !form.address.trim()) {
      toast.error("Give the property a label or an address");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_id: customer.id,
        label: form.label.trim(),
        address: (form.address || "").trim(),
        latitude: form.latitude,
        longitude: form.longitude,
        notes: form.notes || "",
        provider: form.latitude != null ? "mapbox" : "manual",
      };
      if (editing) {
        await api.put(`/estimator/properties/${editing.id}`, payload);
        toast.success("Property updated");
      } else {
        await api.post("/estimator/properties", payload);
        toast.success("Property added");
      }
      setOpen(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to save property";
      toast.error(detail);
    } finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(
      `Delete "${p.label || p.address || "this property"}"?\n\n` +
      `Any measurements saved on it will also be removed. Linked estimates keep their historical snapshot.`
    )) return;
    try {
      await api.delete(`/estimator/properties/${p.id}`);
      toast.success("Property deleted");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error(typeof detail === "string" ? detail : "Property is linked to measurements. Unlink first.", { duration: 6000 });
      } else {
        toast.error("Delete failed");
      }
    }
  };

  const measureOnEstimate = (p) => {
    // Route to the Smart Estimator with the property pre-selected. The
    // estimator reads `?property_id=` on mount and every saved measurement
    // is scoped to that property.
    nav(`/app/customers/${customer.id}/estimator?property_id=${p.id}`);
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="customer-properties-section">
      <div className="flex items-center gap-2 mb-4">
        <Home className="w-5 h-5 text-[#FF5A00]" />
        <h2 className="font-heading text-xl font-bold">Properties</h2>
        <span className="text-xs text-zinc-500 font-mono-accent" data-testid="customer-properties-count">
          {items.length} / {MAX_PROPERTIES}
        </span>
        <Button
          onClick={openNew}
          disabled={atLimit}
          className="ml-auto bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift disabled:opacity-50"
          data-testid="customer-properties-add-btn"
        >
          <Plus className="w-4 h-4 mr-1"/> Add property
        </Button>
      </div>

      {atLimit && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800 mb-3" data-testid="customer-properties-limit-hint">
          This customer is at the {MAX_PROPERTIES}-property limit. Delete one to add another.
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500 py-3">Loading properties…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center" data-testid="customer-properties-empty">
          No properties on file. Add one to start measuring and building estimates for this customer.
        </div>
      ) : (
        <div className="grid gap-2 md:grid-cols-2" data-testid="customer-properties-grid">
          {items.map((p) => (
            <div
              key={p.id}
              className="border border-zinc-200 rounded-lg p-3 hover:border-[#FF5A00] transition-colors"
              data-testid={`customer-property-${p.id}`}
            >
              <div className="flex items-start gap-2">
                <div className="w-9 h-9 shrink-0 rounded-md bg-orange-50 border border-orange-200 text-[#FF5A00] flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate" data-testid={`customer-property-label-${p.id}`}>
                    {p.label || "Property"}
                  </div>
                  <div className="text-xs text-zinc-500 truncate" data-testid={`customer-property-address-${p.id}`}>
                    {p.address || "No address"}
                  </div>
                  {p.notes && <div className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{p.notes}</div>}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  onClick={() => measureOnEstimate(p)}
                  className="bg-zinc-900 hover:bg-zinc-800 text-white text-xs h-7 px-2"
                  data-testid={`customer-property-measure-${p.id}`}
                >
                  <Ruler className="w-3 h-3 mr-1"/> Measure on estimate
                </Button>
                <button
                  onClick={() => openEdit(p)}
                  className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-[#FF5A00] border border-zinc-200 hover:border-[#FF5A00] rounded-md px-2 py-1 h-7"
                  data-testid={`customer-property-edit-${p.id}`}
                >
                  <Pencil className="w-3 h-3"/> Edit
                </button>
                <button
                  onClick={() => del(p)}
                  className="ml-auto inline-flex items-center text-xs text-zinc-400 hover:text-red-600 p-1"
                  title="Delete property"
                  data-testid={`customer-property-delete-${p.id}`}
                >
                  <Trash2 className="w-4 h-4"/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(v)=>{ if (!v) setOpen(false); }}>
        <DialogContent className="sm:max-w-lg" data-testid="customer-property-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editing ? "Edit property" : "Add property"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Label</Label>
              <Input
                value={form.label}
                onChange={(e)=>setForm({...form, label: e.target.value})}
                placeholder="Main house, cabin, lot 42…"
                data-testid="customer-property-form-label"
                autoFocus
              />
            </div>
            <div>
              <Label>Address</Label>
              <AddressAutocomplete
                value={form.address}
                onChange={(v)=>setForm({...form, address: v})}
                onSelect={(p)=>setForm({...form, address: p.formatted, latitude: p.lat, longitude: p.lng})}
                data-testid="customer-property-form-address"
              />
              <div className="text-[11px] text-zinc-500 mt-1">
                Optional. If provided, measurements you save on this property will inherit it for future reuse.
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e)=>setForm({...form, notes: e.target.value})}
                placeholder="Gate code, dog on premise, backyard access from side…"
                data-testid="customer-property-form-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setOpen(false)} disabled={saving} data-testid="customer-property-form-cancel">Cancel</Button>
              <Button type="submit" className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" disabled={saving} data-testid="customer-property-form-submit">
                {saving ? "Saving…" : (editing ? "Save changes" : "Add property")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
